import React, { useState, useEffect } from 'react';
import { Upload, Calendar } from 'lucide-react';
import useApi from "../../utils/useApi";

const IdUploader = ({ user, uid, onRefresh, idType = 'id' }) => {
    const [uploading, setUploading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [expirationDate, setExpirationDate] = useState('');
    const { uploadId, deleteId } = useApi();

    const isSpouseId = idType === 'spouse_id';
    const expirationField = isSpouseId ? 'spouse_identification_expiration_date' : 'identification_expiration_date';
    const labelText = isSpouseId ? 'Spouse Identification Documents' : 'Identification Documents';
    const dateLabel = isSpouseId ? 'Spouse ID Expiration Date' : 'ID Expiration Date';
    const successMessage = isSpouseId ? 'Spouse ID document uploaded successfully' : 'ID document uploaded successfully';

    // Set initial expiration date from user profile if available
    useEffect(() => {
        const profileDate = user?.profile?.[expirationField];
        if (profileDate) {
            const [month, day, year] = profileDate.split('/');
            const formattedDate = `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
            setExpirationDate(formattedDate);
        }
    }, [user, expirationField]);

    const resetStates = () => {
        setError('');
        setSuccess('');
        setUploading(false);
    };

    // Add date validation
    const validateDate = () => {
        const profileDate = user?.profile?.[expirationField];
        if (!expirationDate && !profileDate) {
            setError(`Please set an expiration date for the ${isSpouseId ? 'Spouse ID' : 'ID'}`);
            return false;
        }

        const selectedDate = new Date(expirationDate);
        const today = new Date();

        if (selectedDate < today) {
            setError('Expiration date cannot be in the past');
            return false;
        }

        return true;
    };

    const validateAndUpload = async (file) => {
        resetStates();

        // First validate the date
        if (!validateDate()) {
            return false;
        }

        const validTypes = [
            'image/jpeg',
            'image/jpg',
            'image/png',
            'image/webp',
            'image/heic',
            'image/heif',
            'application/pdf'
        ];

        if (!validTypes.includes(file.type)) {
            setError('Please upload a supported image format (JPG, PNG, WEBP, HEIC) or PDF');
            return false;
        }

        const maxSize = 5 * 1024 * 1024;
        if (file.size > maxSize) {
            setError('File size must be less than 5MB');
            return false;
        }

        setUploading(true);
        try {
            const formData = new FormData();
            formData.append('id_document', file);
            formData.append('uid', user.user_type === 'admin' ? uid : user.id);
            formData.append('type', idType);
            formData.append('identification_expiration_date', expirationDate || user?.profile?.[expirationField]);

            const response = await uploadId(formData);

            if (response.success) {
                setSuccess(successMessage);
                onRefresh();
                document.getElementById('id-display')?.scrollIntoView();
            } else {
                throw new Error(response.message || `Failed to upload ${isSpouseId ? 'Spouse ID' : 'ID'} document`);
            }
        } catch (err) {
            setError(err.message || `An error occurred while uploading the ${isSpouseId ? 'Spouse ID' : 'ID'} document`);
            console.error('Upload error:', err);
        } finally {
            setUploading(false);
        }

        return true;
    };

    const handleFileChange = async (event) => {
        const file = event.target.files[0];
        if (!file) return;

        const didUpload = await validateAndUpload(file);
        if (!didUpload) {
            event.target.value = '';
        } else {
            event.target.value = '';
        }
    };

    const handleDragOver = (event) => {
        event.preventDefault();
        event.stopPropagation();
    };

    const handleDrop = async (event) => {
        event.preventDefault();
        event.stopPropagation();

        const file = event.dataTransfer.files[0];
        if (!file) return;

        await validateAndUpload(file);
    };

    return (
        <div className="w-full mb-6 bg-white dark:bg-gray-800 rounded-lg shadow-sm overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                    {labelText}
                </h2>
            </div>

            <div className="p-6">
                <div className="flex flex-col gap-4">
                    {error && (
                        <div className="p-4 rounded-lg bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800">
                            <p className="text-sm text-red-700 dark:text-red-400">{error}</p>
                        </div>
                    )}

                    {success && (
                        <div className="p-4 rounded-lg bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-800">
                            <p className="text-sm text-green-700 dark:text-green-400">{success}</p>
                        </div>
                    )}

                    {/* Date Input Field */}
                    <div className="flex flex-col gap-2">
                        <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                            {dateLabel} {!user?.profile?.[expirationField] && <span className="text-red-500">*</span>}
                        </label>
                        <div className="relative">
                            <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                            <input
                                type="date"
                                className="pl-10 w-full rounded-md border border-gray-300 dark:border-gray-600
                                         bg-white dark:bg-gray-700 py-2 text-gray-900 dark:text-gray-100
                                         focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                value={expirationDate || user?.profile?.[expirationField] || ''}
                                onChange={(e) => setExpirationDate(e.target.value)}
                                min={new Date().toISOString().split('T')[0]}
                            />
                        </div>
                    </div>

                    <label
                        className="relative group cursor-pointer"
                        onDragOver={handleDragOver}
                        onDrop={handleDrop}
                    >
                        <div className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed rounded-lg
                            border-gray-300 dark:border-gray-600
                            bg-gray-50 dark:bg-gray-700/50
                            group-hover:bg-gray-100 dark:group-hover:bg-gray-700
                            transition-colors duration-200">
                            <div className="flex flex-col items-center justify-center pt-5 pb-6">
                                <Upload className="w-8 h-8 mb-2 text-gray-400 dark:text-gray-500" />
                                <p className="mb-2 text-sm text-gray-500 dark:text-gray-400">
                                    <span className="font-semibold">Click to upload</span> or drag and drop
                                </p>
                                <p className="text-xs text-gray-500 dark:text-gray-400">
                                    JPG, PNG, WEBP, HEIC or PDF (max. 5MB)
                                </p>
                            </div>
                        </div>
                        <input
                            type="file"
                            className="hidden"
                            accept=".jpg,.jpeg,.png,.webp,.heic,.heif,.pdf"
                            onChange={handleFileChange}
                            disabled={uploading}
                        />
                    </label>

                    {uploading && (
                        <div className="flex items-center justify-center py-2">
                            <div className="animate-spin rounded-full h-6 w-6 border-2 border-gray-300 dark:border-gray-600 border-t-blue-600 dark:border-t-blue-400"></div>
                            <span className="ml-2 text-gray-700 dark:text-gray-300">Uploading...</span>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default IdUploader;
